import { MouseEvent } from 'react'
import Kedo from '@/Kedo'
import { isInteractiveClick } from '@/util/clickGuards'
import { Block, Dossier, DisplayItem, ContentItem } from '../types'

export const navigateToDossier = (dossierId: number): void => {
    window.location.href = `/contentdossier/${dossierId}`
}

const contentClickSelector =
    '.contentData, .displayItem, .list-group-item, .badge'
export const handleContentClick = (
    event: MouseEvent<HTMLElement>,
    dossier: Dossier
): boolean | void => {
    const { target, currentTarget } = event

    if (!(target instanceof Element) || !(currentTarget instanceof Element)) {
        return false
    }

    if (isInteractiveClick(event)) {
        return false
    }

    if (
        !target.closest(contentClickSelector) &&
        !currentTarget.closest(contentClickSelector)
    ) {
        return false
    }

    window.location.replace(`/contentdossier/${dossier.id}`)
}

export const getContentValue = (
    listItem: DisplayItem,
    dossierData: ContentItem[],
    _dossier: Dossier
): unknown | null => {
    if (!dossierData || !listItem) return null

    const singleDossierData = dossierData.find(
        (data) => data.content.id === listItem.id
    )

    if (!singleDossierData?.content) return null

    return singleDossierData.content.content
}

export const getImageUrl = (
    imageField: DisplayItem,
    dossierData: ContentItem[],
    kedo: Kedo
): string | null => {
    if (!dossierData) return null

    const singleDossierData = dossierData.find(
        (data) => data.content.id === imageField.id
    )

    if (!singleDossierData?.content?.content) return null

    const content = singleDossierData.content.content as Array<{ id: number }>

    if (!content || content.length === 0) return null

    const baseUrl = kedo.api().getBaseUrl()
    return `${baseUrl}/file/${content[0].id}/download`
}

export const translateDisplayItem = (item: DisplayItem, kedo: Kedo): string => {
    return kedo.env().translateItem(item, 'displayitem')
}

export const translateBlock = (block: Block, kedo: Kedo): string => {
    return kedo.env().translateItem(block, 'mypageblock')
}

export const getBlockLimit = (block: Block): number => {
    const limit = block.settings?.limit

    if (typeof limit === 'string' && limit.length > 0) {
        return parseInt(limit, 10)
    }

    if (typeof limit === 'number') {
        return limit
    }

    return 10
}

export const shouldShowAddButton = (
    block: Block,
    settingsLink?: { hide_add?: boolean }
): boolean => {
    return block.settings?.show_add === true && settingsLink?.hide_add !== true
}

export const getSortParams = (
    sort: Array<{ dddfId: number; sort: string }> | undefined
): { sort: number[]; sortOrder: string[] } | null => {
    if (!sort || !Array.isArray(sort) || sort.length === 0) {
        return null
    }

    const sortIds: number[] = []
    const sortOrders: string[] = []

    sort.forEach((item) => {
        sortIds.push(item.dddfId)
        sortOrders.push(item.sort)
    })

    return { sort: sortIds, sortOrder: sortOrders }
}

export const getColSize = (
    col: number | undefined,
    type: 'xs' | 'md' | 'lg'
): number => {
    switch (type) {
        case 'xs':
            return 12
        case 'md':
            return col && col >= 6 && col <= 12 ? col : 12
        case 'lg':
            return col && col > 0 && col <= 12 ? col : 12
        default:
            return 12
    }
}

export const formatDate = (
    date: Date | string,
    format: string,
    locale = 'en-US'
): string => {
    const d = typeof date === 'string' ? new Date(date) : date

    const options: Intl.DateTimeFormatOptions = {}

    switch (format) {
        case 'year_month':
            options.year = 'numeric'
            options.month = '2-digit'
            break
        case 'quarter':
            options.year = 'numeric'
            break
        case 'year':
            options.year = 'numeric'
            break
        case 'day':
            options.weekday = 'short'
            break
        case 'date':
            options.day = '2-digit'
            break
        case 'full':
            options.year = 'numeric'
            options.month = 'long'
            options.day = '2-digit'
            break
        default:
            options.year = 'numeric'
            options.month = '2-digit'
            options.day = '2-digit'
    }

    return new Intl.DateTimeFormat(locale, options).format(d)
}

export const isPieChart = (block: Block): boolean => {
    const chartFormat = block.settings?.chart_format

    if (chartFormat === 'pie') return true

    if (
        !block.settings?.use_creation_date &&
        !block.settings?.date_field &&
        chartFormat !== 'table'
    ) {
        return true
    }

    return false
}

export const isDateOriented = (block: Block): boolean => {
    return Boolean(
        block.settings?.use_creation_date || block.settings?.date_field
    )
}

export const getChartHeight = (block: Block, isContainer = false): string => {
    if (isContainer || isPieChart(block)) {
        return '300px'
    }
    return '100%'
}

export const getMinHeight = (
    dataLength: number,
    chartFormat: string | null | undefined
): string => {
    if (dataLength > 0 && chartFormat !== 'table') {
        return '300px'
    }
    return 'auto'
}

export const createClassName = (
    ...classes: (string | boolean | undefined | null)[]
): string => {
    return classes.filter(Boolean).join(' ')
}

export const debounce = <T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | null = null

    return (...args: Parameters<T>) => {
        if (timeout) {
            clearTimeout(timeout)
        }
        timeout = setTimeout(() => func(...args), wait)
    }
}

export const getBlockStyleConfig = (
    dashboard: boolean | undefined,
    container: boolean | undefined,
    isPie: boolean,
    chartFormat?: string | null,
    dataLength = 0
) => ({
    containerStyle: {
        height: container || isPie ? '300px' : '100%',
        marginBottom: '30px',
        minHeight: dataLength > 0 && chartFormat !== 'table' ? '300px' : 'auto',
        minWidth: '300px',
    },
    className: !dashboard ? 'kedo-block' : undefined,
})
